import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from '../../app.component';
import { appConfig } from '../../app.config';
import { CONFIG_RETRY, ConfigErrorComponent } from '../../bootstrap-error/config-error.component';
import { APP_CONFIG, AppConfig } from './app-config.model';
import { loadAppConfig } from './load-app-config';

interface BootstrappedApplication {
  destroy?(): void;
}

export interface StartApplicationDeps {
  /** Defaults to the real /config.json loader. */
  readonly load?: () => Promise<AppConfig>;
  /** Defaults to bootstrapApplication(AppComponent, …) with APP_CONFIG. */
  readonly bootstrapShell?: (config: AppConfig) => Promise<BootstrappedApplication>;
  /** Defaults to bootstrapApplication(ConfigErrorComponent, …) with CONFIG_RETRY. */
  readonly bootstrapError?: (retry: () => void) => Promise<BootstrappedApplication>;
}

/**
 * Load → validate → bootstrap orchestration (R-001, FR-033/FR-034). Success
 * bootstraps the shell with APP_CONFIG; every failure mode bootstraps the
 * dependency-free ConfigErrorComponent with CONFIG_RETRY bound to a re-run of
 * the same sequence. No console.error on either path (FR-042); no defaults
 * are ever substituted.
 */
export async function startApplication(deps: StartApplicationDeps = {}): Promise<void> {
  const load = deps.load ?? loadAppConfig;
  const bootstrapShell = deps.bootstrapShell ?? defaultBootstrapShell;
  const bootstrapError = deps.bootstrapError ?? defaultBootstrapError;
  let activeApplication: BootstrappedApplication | null = null;
  let attemptInFlight = false;

  const attempt = async (): Promise<void> => {
    if (attemptInFlight) return;
    attemptInFlight = true;

    try {
      activeApplication?.destroy?.();
      activeApplication = null;
      const config = await load();
      activeApplication = await bootstrapShell(config);
    } catch {
      // The failure is rendered to the user by ConfigErrorComponent; nothing
      // is logged to the console (FR-042).
      activeApplication = await bootstrapError(() => void attempt());
    } finally {
      attemptInFlight = false;
    }
  };

  await attempt();
}

function defaultBootstrapShell(config: AppConfig): Promise<BootstrappedApplication> {
  return bootstrapApplication(AppComponent, {
    ...appConfig,
    providers: [...appConfig.providers, { provide: APP_CONFIG, useValue: config }],
  });
}

function defaultBootstrapError(retry: () => void): Promise<BootstrappedApplication> {
  return bootstrapApplication(ConfigErrorComponent, {
    providers: [{ provide: CONFIG_RETRY, useValue: retry }],
  });
}
